import React, { useEffect, useState } from 'react';
import { API_CONFIG } from '../../config/api.config';
import Button from '../Form/Button';
import { resolveDocumentExpiry } from '../../pages/CrewAndCarsDocuments/documentHelpers';

export interface EntityDocument {
  id: number;
  documentId: string;
  documentType: string;
  documentNumber?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  filePath?: string | null;
  status?: string;
}

interface EntityDocumentsModalProps {
  title: string;
  fetchDocuments: () => Promise<EntityDocument[]>;
  onClose: () => void;
}

const fileUrl = (path: string): string =>
  path.startsWith('http') ? path : `${API_CONFIG.BASE_URL}${path}`;

const formatDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString('ar-EG') : '-';

const statusBadge = (status?: string) => {
  const styles: Record<string, string> = {
    valid: 'bg-green-100 text-green-800',
    expiring_soon: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-red-100 text-red-800',
  };
  const labels: Record<string, string> = {
    valid: 'سارية',
    expiring_soon: 'قرب الانتهاء',
    expired: 'منتهية',
  };
  if (!status || !styles[status]) return <span>-</span>;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

/**
 * Read-only viewer for the documents attached to an employee or a car
 * (national IDs, car licenses...). Each row links to the stored file.
 */
const EntityDocumentsModal: React.FC<EntityDocumentsModalProps> = ({ title, fetchDocuments, onClose }) => {
  const [documents, setDocuments] = useState<EntityDocument[] | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let active = true;
    fetchDocuments()
      .then((docs) => { if (active) setDocuments(docs); })
      .catch((e) => { if (active) setError(e?.message || 'Failed to load documents'); });
    return () => { active = false; };
  }, [fetchDocuments]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <Button onClick={onClose} variant="secondary" size="sm">إغلاق</Button>
        </div>
        <div className="p-4">
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!error && documents === null && <p className="text-gray-500 text-sm">جارِ التحميل...</p>}
          {!error && documents !== null && documents.length === 0 && (
            <p className="text-gray-500 text-sm">لا توجد مستندات مسجلة</p>
          )}
          {!error && documents !== null && documents.length > 0 && (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">النوع</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">الرقم</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">تاريخ الانتهاء</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">الحالة</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">الملف</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.documentId || doc.id}>
                    <td className="px-4 py-2 text-gray-900">{doc.documentType || '-'}</td>
                    <td className="px-4 py-2 text-gray-900">{doc.documentNumber || '-'}</td>
                    <td className="px-4 py-2 text-gray-900">{formatDate(resolveDocumentExpiry(doc.documentType, doc, (doc as any).employee || (doc as any).car))}</td>
                    <td className="px-4 py-2">{statusBadge(doc.status)}</td>
                    <td className="px-4 py-2">
                      {doc.filePath ? (
                        <a
                          href={fileUrl(doc.filePath)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-ocean-600 hover:text-ocean-800 underline"
                        >
                          عرض الملف
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntityDocumentsModal;
